import React, { useRef, useEffect, useMemo, useCallback } from 'react';
import mapboxgl from 'mapbox-gl';
import polylineLib from '@mapbox/polyline';
import 'mapbox-gl/dist/mapbox-gl.css';

const MAPBOX_ACCESS_TOKEN = 'pk.eyJ1IjoiZGFuaWVsa2FsZG9yIiwiYSI6ImNtYjRyNTYzczA1emcya3FwdzhoMmhmdGIifQ.qkeEKxO6j5ugrpbdC06grA';

if (typeof mapboxgl !== 'undefined') {
  mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;
}

const getActivityEmoji = (type) => {
  switch (type) {
    case 'ride':
    case 'virtualride': return '🚴';
    case 'run': return '🏃';
    case 'walk': return '🚶';
    case 'hike': return '🥾';
    case 'swim': return '🏊';
    case 'sailing': return '⛵';
    case 'windsurfing': return '🏄';
    case 'kayak': return '🛶';
    default: return '🏃';
  }
};

const ProgressMap = ({ polyline, progressPercentage, activityType, onLoad }) => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const markerInnerRef = useRef(null);
  const animatedProgressRef = useRef(0);
  const targetProgressRef = useRef(0);
  const smoothedAngleRef = useRef(null); // smoothed screen-space angle (degrees)
  const rafIdRef = useRef(null);
  const mapReadyRef = useRef(false);

  // Decode polyline once
  const coordinates = useMemo(() => {
    if (!polyline) return [];
    try {
      return polylineLib.decode(polyline).map(([lat, lng]) => [lng, lat]);
    } catch (e) {
      console.error('Failed to decode polyline:', e);
      return [];
    }
  }, [polyline]);

  // Pre-compute cumulative arc-length distances so progress maps to actual
  // route distance rather than coordinate index (GPS points are time-sampled,
  // so index-based mapping bunches up in slow sections).
  const cumulativeDistances = useMemo(() => {
    if (coordinates.length < 2) return [];
    const dists = [0];
    for (let i = 1; i < coordinates.length; i++) {
      const dx = coordinates[i][0] - coordinates[i - 1][0];
      const dy = coordinates[i][1] - coordinates[i - 1][1];
      dists.push(dists[i - 1] + Math.sqrt(dx * dx + dy * dy));
    }
    return dists;
  }, [coordinates]);

  // Interpolate a position between two coordinates
  const lerpCoord = useCallback((a, b, t) => [
    a[0] + (b[0] - a[0]) * t,
    a[1] + (b[1] - a[1]) * t,
  ], []);

  // Update map position (completed route + marker) with distance-based interpolation
  const updateMapPosition = useCallback((progress) => {
    if (!mapRef.current || !mapReadyRef.current || coordinates.length < 2 || cumulativeDistances.length < 2) return;

    const map = mapRef.current;
    const clampedProgress = Math.max(0, Math.min(100, progress));

    // Map progress to actual route distance
    const totalDist = cumulativeDistances[cumulativeDistances.length - 1];
    const targetDist = (clampedProgress / 100) * totalDist;

    // Binary search for the segment containing targetDist
    let lo = 0;
    let hi = cumulativeDistances.length - 1;
    while (lo < hi - 1) {
      const mid = (lo + hi) >> 1;
      if (cumulativeDistances[mid] <= targetDist) lo = mid;
      else hi = mid;
    }
    const safeBase = lo;
    const safeNext = Math.min(lo + 1, coordinates.length - 1);

    // Fraction within this segment
    const segLen = cumulativeDistances[safeNext] - cumulativeDistances[safeBase];
    const fraction = segLen > 0 ? (targetDist - cumulativeDistances[safeBase]) / segLen : 0;

    // Interpolated position
    const puckPos = lerpCoord(coordinates[safeBase], coordinates[safeNext], fraction);

    // Build completed route up to and including the interpolated point
    const completedCoords = [
      ...coordinates.slice(0, safeBase + 1),
      puckPos,
    ];

    // Update completed route
    const source = map.getSource('completed-route');
    if (source) {
      source.setData({
        type: 'Feature',
        properties: {},
        geometry: { type: 'LineString', coordinates: completedCoords },
      });
    }

    // Update marker position
    if (markerRef.current) {
      markerRef.current.setLngLat(puckPos);
    }

    // Rotate marker to face direction of travel (smoothed)
    if (markerInnerRef.current) {
      // Pick direction segment — use previous segment at route end
      let dirFrom, dirTo;
      if (safeBase !== safeNext) {
        dirFrom = coordinates[safeBase];
        dirTo = coordinates[safeNext];
      } else if (safeBase > 0) {
        dirFrom = coordinates[safeBase - 1];
        dirTo = coordinates[safeBase];
      } else {
        return; // can't determine direction
      }

      // Screen-space direction (lng right = +x, lat up but screen y down = invert)
      const dx = dirTo[0] - dirFrom[0];
      const dy = -(dirTo[1] - dirFrom[1]);

      if (Math.abs(dx) < 1e-10 && Math.abs(dy) < 1e-10) return;

      // Target angle from positive x-axis (right), CW positive
      const targetAngle = Math.atan2(dy, dx) * (180 / Math.PI);

      // Smooth the angle — interpolate via shortest arc
      let displayAngle;
      if (smoothedAngleRef.current === null) {
        // First frame: snap immediately
        smoothedAngleRef.current = targetAngle;
        displayAngle = targetAngle;
      } else {
        // Compute shortest-path difference, wrapped to [-180, 180]
        let diff = targetAngle - smoothedAngleRef.current;
        diff = ((diff + 180) % 360 + 360) % 360 - 180;

        // Exponential decay: ~95% closed in ~0.3s at 60fps
        // lerp factor per frame ≈ 0.15
        const lerpFactor = 0.15;
        smoothedAngleRef.current += diff * lerpFactor;

        // Keep smoothed angle in [-180, 180] range
        smoothedAngleRef.current = ((smoothedAngleRef.current + 180) % 360 + 360) % 360 - 180;
        displayAngle = smoothedAngleRef.current;
      }

      // Emoji default faces LEFT (180° in CSS angles).
      // scaleX(-1) rotate(θ) → resulting direction = -θ
      // rotate(θ) alone     → resulting direction = 180 + θ
      if (displayAngle > -90 && displayAngle <= 90) {
        // Moving rightward: flip emoji to face right, rotate to match angle
        markerInnerRef.current.style.transform = `scaleX(-1) rotate(${-displayAngle}deg)`;
      } else {
        // Moving leftward: emoji already faces left, rotate to match angle
        markerInnerRef.current.style.transform = `rotate(${displayAngle - 180}deg)`;
      }
    }
  }, [coordinates, cumulativeDistances, lerpCoord]);

  // Update target progress when prop changes
  useEffect(() => {
    targetProgressRef.current = progressPercentage;
  }, [progressPercentage]);

  // Initialise map
  useEffect(() => {
    if (!containerRef.current || coordinates.length < 2) return;

    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: 'mapbox://styles/mapbox/light-v11',
      interactive: false,
      attributionControl: false,
      logoPosition: 'bottom-left',
      fadeDuration: 0,
    });

    mapRef.current = map;

    map.on('load', () => {
      // Mute base map layers for a soft grey monochrome look
      map.getStyle().layers.forEach(layer => {
        try {
          if (layer.type === 'background') {
            map.setPaintProperty(layer.id, 'background-color', '#e0e0e0');
          } else if (layer.type === 'fill') {
            map.setPaintProperty(layer.id, 'fill-opacity', 0.3);
            map.setPaintProperty(layer.id, 'fill-color', '#c0c0c0');
          } else if (layer.type === 'line') {
            map.setPaintProperty(layer.id, 'line-opacity', 0.2);
          } else if (layer.type === 'symbol') {
            map.setPaintProperty(layer.id, 'text-opacity', 0.35);
            map.setPaintProperty(layer.id, 'text-color', '#888888');
            map.setPaintProperty(layer.id, 'icon-opacity', 0.25);
          } else if (layer.type === 'fill-extrusion') {
            map.setPaintProperty(layer.id, 'fill-extrusion-opacity', 0.2);
          }
        } catch (e) { /* ignore layers that don't support the property */ }
      });

      // Full route (light grey, subtle)
      map.addSource('full-route', {
        type: 'geojson',
        data: {
          type: 'Feature',
          properties: {},
          geometry: { type: 'LineString', coordinates },
        },
      });
      map.addLayer({
        id: 'full-route',
        type: 'line',
        source: 'full-route',
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: { 'line-color': '#aaaaaa', 'line-width': 3 },
      });

      // Completed route (orange)
      map.addSource('completed-route', {
        type: 'geojson',
        data: {
          type: 'Feature',
          properties: {},
          geometry: { type: 'LineString', coordinates: [coordinates[0], coordinates[0]] },
        },
      });
      map.addLayer({
        id: 'completed-route',
        type: 'line',
        source: 'completed-route',
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: { 'line-color': '#FC5200', 'line-width': 4 },
      });

      // Fit map to route
      const bounds = new mapboxgl.LngLatBounds();
      coordinates.forEach((coord) => bounds.extend(coord));
      map.fitBounds(bounds, { padding: 40, duration: 0 });

      // Start marker (green circle)
      const startEl = document.createElement('div');
      startEl.className = 'progress-start-marker';
      startEl.textContent = '🟢';
      new mapboxgl.Marker({ element: startEl, anchor: 'center' })
        .setLngLat(coordinates[0])
        .addTo(map);

      // Finish marker (chequered flag)
      const finishEl = document.createElement('div');
      finishEl.className = 'progress-finish-marker';
      finishEl.textContent = '🏁';
      new mapboxgl.Marker({ element: finishEl, anchor: 'center' })
        .setLngLat(coordinates[coordinates.length - 1])
        .addTo(map);

      // Add activity icon marker — inner span holds the emoji + rotation
      // so transforms don't conflict with Mapbox's positioning on the outer element.
      const markerEl = document.createElement('div');
      markerEl.className = 'progress-activity-marker';
      const inner = document.createElement('span');
      inner.className = 'progress-activity-marker-inner';
      inner.textContent = getActivityEmoji(activityType);
      markerEl.appendChild(inner);
      markerInnerRef.current = inner;

      const marker = new mapboxgl.Marker({ element: markerEl })
        .setLngLat(coordinates[0])
        .addTo(map);
      markerRef.current = marker;

      mapReadyRef.current = true;

      // Update to current progress immediately
      updateMapPosition(animatedProgressRef.current);

      if (onLoad) onLoad();
    });

    // Animation loop — spring-damped lerp for smooth, fluid motion
    let lastTime = performance.now();
    const animate = (now) => {
      const dt = Math.min((now - lastTime) / 1000, 0.1); // delta in seconds, capped
      lastTime = now;

      const current = animatedProgressRef.current;
      const target = targetProgressRef.current;
      const diff = target - current;

      if (Math.abs(diff) > 0.01) {
        // Smooth exponential decay: ~95% of the gap closed in ~0.8s
        const smoothing = 1 - Math.pow(0.03, dt);
        animatedProgressRef.current += diff * smoothing;
        updateMapPosition(animatedProgressRef.current);
      }

      rafIdRef.current = requestAnimationFrame(animate);
    };
    rafIdRef.current = requestAnimationFrame(animate);

    return () => {
      if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
      if (markerRef.current) markerRef.current.remove();
      markerInnerRef.current = null;
      smoothedAngleRef.current = null;
      mapReadyRef.current = false;
      map.remove();
      mapRef.current = null;
    };
  }, [coordinates, activityType, onLoad, updateMapPosition]);

  if (coordinates.length < 2) return null;

  return (
    <div
      ref={containerRef}
      className="progress-map"
      style={{ width: '100%', height: '100%', position: 'absolute', top: 0, left: 0 }}
    />
  );
};

export default ProgressMap;
